import { Plus, Trash } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Panel } from "@/components/admin/AdminUI";
import { PACK_BADGES } from "@/lib/pricing";

function Cell({ row, field, errors, onChange, className = "", prefix, ...rest }) {
  const err = errors?.[field];
  const id = `pack-${row.k}-${field}`;
  return (
    <td className={`px-2 py-3 align-top ${className}`}>
      <div className="relative">
        {prefix && <span className="cw-muted pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm">{prefix}</span>}
        <Input id={id} value={row[field]} onChange={(e) => onChange(field, e.target.value)} aria-invalid={!!err} aria-label={`${field.replace(/_/g, " ")}`}
          className={`h-10 rounded-lg bg-white tabular-nums ${prefix ? "pl-6" : ""} ${err ? "border-destructive" : ""}`} data-testid={`pack-${field}-input-${row.k}`} {...rest} />
      </div>
      {err && <p className="cw-error mt-1 text-xs" role="alert" data-testid={`pack-${field}-error-${row.k}`}>{err}</p>}
    </td>
  );
}

export default function PackTable({ group, rows, errors, onChange, onAdd, onRemove }) {
  return (
    <Panel className="overflow-hidden" data-testid={`pricing-group-${group.key}`}>
      <div className="flex items-center justify-between gap-3 px-6 pb-2 pt-5">
        <div><h2 className="font-semibold">{group.title}</h2><p className="cw-muted text-xs">{group.hint} · {rows.length} pack{rows.length === 1 ? "" : "s"}</p></div>
        <Button size="sm" variant="outline" onClick={onAdd} data-testid={`pricing-add-pack-${group.key}`}><Plus />Add pack</Button>
      </div>
      {rows.length ? (
        <div className="overflow-x-auto px-4 pb-3">
          <table className="w-full min-w-[880px] text-left" data-testid={`pricing-table-${group.key}`}>
            <thead><tr className="cw-muted text-xs">
              <th className="px-2 py-2 font-medium">Quantity</th><th className="px-2 py-2 font-medium">Price (USD)</th>
              <th className="px-2 py-2 font-medium">Store product ID</th><th className="px-2 py-2 font-medium">Active</th>
              <th className="px-2 py-2 font-medium">Order</th>
              <th className="w-40 px-2 py-2 font-medium">Badge<span className="block font-normal" data-testid={`pack-badge-help-${group.key}`}>Shown on the pack card in the app.</span></th><th className="w-12" />
            </tr></thead>
            <tbody className="divide-y divide-border">
              {rows.map((r) => (
                <tr key={r.k} data-testid={`pack-row-${r.k}`}>
                  <Cell row={r} field="quantity" errors={errors[r.k]} onChange={(f, v) => onChange(r.k, f, v.replace(/\D/g, ""))} inputMode="numeric" className="w-28" />
                  <Cell row={r} field="price" errors={errors[r.k]} onChange={(f, v) => onChange(r.k, f, v.replace(/[^\d.]/g, ""))} inputMode="decimal" prefix="$" className="w-36" />
                  <Cell row={r} field="store_product_id" errors={errors[r.k]} onChange={(f, v) => onChange(r.k, f, v)} placeholder="Not linked yet" maxLength={140} />
                  <td className="px-2 py-3 align-top"><div className="flex h-10 items-center">
                    <Switch checked={r.active} onCheckedChange={(v) => onChange(r.k, "active", v)} aria-label="Active" data-testid={`pack-active-toggle-${r.k}`} />
                  </div></td>
                  <Cell row={r} field="order" errors={errors[r.k]} onChange={(f, v) => onChange(r.k, f, v.replace(/\D/g, ""))} inputMode="numeric" className="w-24" />
                  <td className="px-2 py-3 align-top">
                    <select value={r.badge} onChange={(e) => onChange(r.k, "badge", e.target.value)} aria-label="Badge"
                      className="h-10 w-full rounded-lg border border-input bg-white px-2 text-sm" data-testid={`pack-badge-select-${r.k}`}>
                      {PACK_BADGES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                    </select>
                  </td>
                  <td className="px-2 py-3 align-top">
                    <Button size="icon" variant="ghost" className="cw-error" onClick={() => onRemove(r.k)} aria-label="Remove pack" data-testid={`pack-remove-button-${r.k}`}><Trash /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <p className="cw-muted px-6 pb-6 pt-2 text-sm" data-testid={`pricing-empty-${group.key}`}>{"No packs. This group can't buy Super Crushes."}</p>}
    </Panel>
  );
}
